import React from 'react'
import MaterialTable from 'material-table'
import hoc from '@hoc'
import PropTypes from 'prop-types'
import * as utils from '@utils'
import { Icon, Button, Switch } from '@material-ui/core'
import { SaveAlt, Edit, Delete } from '@material-ui/icons'
import ConfirmMsgDialog from '@msg/confirm-msg-dialog'
import i18n from '@i18n'
import { CURRENCY } from '@const'
import ExcelJs from "exceljs/dist/exceljs";
import cloneDeep from 'lodash.clonedeep'

const DEFAULT_PRIMARY_KEY = 'id'

class Table extends React.Component {
    constructor(props) {
        super(props)
        this.t = (key) => {
            return utils.t(key, 'Table')
        }
        i18n.on('languageChanged', (_language) => {
            this.setDataDisplay()
            this.setColumns()
        })
    }

    state = {
        data: [],
        dataDisplay: [],
        columns: [],
        actions: [],
        isLoading: false,
        pageSize: 10,
        currency: CURRENCY.DEFAULT_CURRENCY
    }

    translateMap = {}

    shouldComponentUpdate(nextProps, nextState) {
        return (
            nextState.dataDisplay !== this.state.dataDisplay ||
            nextState.isLoading !== this.state.isLoading ||
            (Array.isArray(nextProps.columns) && nextProps.columns.length !== this.materialTable.props.columns.length) ||
            (Array.isArray(nextProps.actions) && nextProps.actions.length !== this.materialTable.props.actions.length)
        )
    }

    componentDidUpdate() {
        if (Array.isArray(this.props.columns) && this.props.columns.length !== this.materialTable.props.columns.length) {
            this.setColumns()
        }

        if (Array.isArray(this.props.actions) && this.props.actions.length !== this.materialTable.props.actions.length) {
            this.setActions()
        }
    }

    componentDidMount() {
        this.setTranslateMap()
        this.setColumns()
        this.setActions()
        this.materialTable.dataManager.changeOrder(
            this.props.options && this.props.options.orderIdx ? this.props.options.orderIdx : 0,
            this.props.options && this.props.options.orderDirection ? this.props.options.orderDirection : 'asc'
        )
        this.props.onRef && this.props.onRef(this)
    }

    componentWillUnmount() {
        this.props.onRef && this.props.onRef(undefined)
    }

    queryData(params, showToast, successCallback, failureCallback) {
        this.setState({ isLoading: true })

        utils.apiRequest(
            'get',
            this.props.queryDataArgs.apiPath,
            params,
            (data) => {
                this.setState({
                    data: data ? Object.values(data) : [],
                    isLoading: false,
                })
                this.setDataDisplay()

                if (typeof this.props.queryDataArgs.callback === 'function') {
                    this.props.queryDataArgs.callback()
                }

                if (typeof successCallback === 'function') {
                    successCallback()
                }
            },
            (_err) => {
                this.setState({
                    data: [],
                    isLoading: false,
                })
                this.setDataDisplay()

                if (typeof this.props.queryDataArgs.failureCallback === 'function') {
                    this.props.queryDataArgs.failureCallback()
                }

                if (typeof failureCallback === 'function') {
                    failureCallback()
                }
            },
            !showToast
        )
    }

    GetData() {
        return this.state.data
    }

    async SetData(data, currency) {
        await this.setState({ data: data, currency: currency }, () => {
            this.setDataDisplay()
            this.SetLoading(false)
        })
    }

    GetLoading() {
        return this.state.isLoading
    }

    SetLoading(bool) {
        this.setState({ isLoading: bool })
    }

    setDataDisplay() {
        // clone object array data
        const newData = cloneDeep(this.state.data)

        // data translate
        for (const idx in newData) {
            for (const mapKey in this.translateMap)
                newData[idx][mapKey] = utils.t(this.translateMap[mapKey].tranKey + '.' + newData[idx][this.translateMap[mapKey].dataKey])
        }

        this.setState({ dataDisplay: newData })
    }

    ShowColumn(field) {
        const columns = this.state.columns
        for (let idx = 0; idx < columns.length; idx++) {
            if (columns[idx].field === field) {
                columns[idx].hidden = false
                break
            }
        }

        this.setState({ columns: columns })
    }

    HideColumn(field) {
        const columns = this.state.columns
        for (let idx = 0; idx < columns.length; idx++) {
            if (columns[idx].field === field) {
                columns[idx].hidden = true
                break
            }
        }

        this.setState({ columns: columns })
    }

    setColumns() {
        const newColumns = []
        for (const idx in this.props.columns) {
            const propsColumn = this.props.columns[idx]
            newColumns[idx] = {
                field: propsColumn.field,
                render: propsColumn.render,
                hideTitle: propsColumn.hideTitle,
                lookup: propsColumn.lookup,
                hidden: propsColumn.hidden,
                export: propsColumn.export === undefined ? true : propsColumn.export,
            }

            if (propsColumn.type) {
                switch (propsColumn.type) {
                    case 'percentage':
                        newColumns[idx].render = (rowData) => {
                            return Math.round(rowData['rtp_value'] * 100) / 100 + '%'
                        }
                        break
                    case 'currency':
                        newColumns[idx].render = (rowData) => {
                            /** 小數後位數 */
                            const decimal = this.props.auth.isCompleteDecimal ? 6 : 2
                            const display = utils.currencyFormatter(rowData[propsColumn.field], decimal, utils.getCurrencyRate(this.state.currency))

                            return propsColumn.render ? propsColumn.render(rowData, display) : display
                        }
                        newColumns[idx].customSort = (a, b) => {
                            const dataKey = propsColumn.field
                            return a[dataKey] - b[dataKey]
                        }
                        break
                    case 'is_support_single':
                        newColumns[idx].render = (rowData) => {
                            return true === rowData.is_support_single ? 'V' : ''
                        }
                        break
                    case 'datetime':
                        newColumns[idx].render = (rowData) => {
                            const display = utils.unixToDateTime(rowData[propsColumn.field])

                            return propsColumn.render ? propsColumn.render(rowData, display) : display
                        }
                        if (propsColumn?.cusSort) {
                            newColumns[idx].customSort = (a, b) => {
                                const dataKey = propsColumn.field
                                return a[dataKey] - b[dataKey]
                            }
                        }
                        break
                    case 'date':
                        newColumns[idx].render = (rowData) => {
                            return utils.unixToDate(rowData[propsColumn.field])
                        }
                        break
                    case 'datemonth':
                        newColumns[idx].render = (rowData) => {
                            return utils.unixToDateMonth(rowData[propsColumn.field])
                        }
                        break
                    case 'time':
                        newColumns[idx].render = (rowData) => {
                            return utils.unixToTime(rowData[propsColumn.field])
                        }
                        break
                    case 'platform':
                        newColumns[idx].render = (rowData) => {
                            if (rowData[propsColumn.field] === 'all') {
                                return this.t('allPlatform')
                            } else if (rowData[propsColumn.field] && rowData[propsColumn.field].indexOf('count') === 0) {
                                const numOfSubPfId = rowData[propsColumn.field].split('_')[1]
                                return this.t('platform') + '(' + numOfSubPfId + ')'
                            }

                            if (rowData[propsColumn.field] && rowData[propsColumn.field].indexOf('public') > 0) {
                                const pfId = rowData[propsColumn.field].split('_')[0]
                                return (
                                    ('Platform.' + pfId === this.t('Platform.' + pfId) ? pfId + this.t('platform') : this.t('Platform.' + pfId)) +
                                    ' ' +
                                    this.t('public')
                                )
                            } else {
                                return utils.GeneratePfSubPfName(rowData[propsColumn.field])
                            }
                        }
                        newColumns[idx].customSort = (a, b) => {
                            if (a.pf_sub_pf_id && b.pf_sub_pf_id) {
                                return utils.SortPlatform(a.pf_sub_pf_id, b.pf_sub_pf_id)
                            }
                        }
                        break
                    case 'number':
                        newColumns[idx].render = (rowData) => {
                            const display = rowData[propsColumn.field]

                            return propsColumn.render ? propsColumn.render(rowData, display) : display
                        }

                        newColumns[idx].customSort = (a, b) => {
                            const dataKey = propsColumn.field
                            return a[dataKey] - b[dataKey]
                        }
                        break
                    case 'numberByThous':
                            newColumns[idx].render = (rowData) => {
                                const display = utils.currencyFormatter(rowData[propsColumn.field], 0)
    
                                return propsColumn.render ? propsColumn.render(rowData, display) : display
                            }
    
                            newColumns[idx].customSort = (a, b) => {
                                const dataKey = propsColumn.field
                                return a[dataKey] - b[dataKey]
                            }
                            break
                    case 'active':
                        newColumns[idx].render = (rowData) => (
                            <Switch
                                checked={rowData[propsColumn.field]}
                                onChange={(event) => {
                                    this.confirmDialog.OpenDialog({
                                        type: 'info',
                                        msg: this.t('confirmSwitchMsg'),
                                        confirmParams: { checked: event.target.checked },
                                        confirm: (params) => {
                                            this.setState({ isLoading: true })
                                            const priKeyObj = propsColumn.priKey
                                                ? { [propsColumn.priKey]: rowData[propsColumn.priKey] }
                                                : {
                                                      [DEFAULT_PRIMARY_KEY]: rowData[DEFAULT_PRIMARY_KEY],
                                                  }
                                            utils.apiRequest(
                                                'post',
                                                propsColumn.apiPath,
                                                {
                                                    ...priKeyObj,
                                                    [propsColumn.field]: params.checked,
                                                    ...propsColumn.apiParams,
                                                },
                                                (_data) => {
                                                    this.confirmDialog.closeDialog()
                                                    this.queryData()
                                                },
                                                (_error) => {
                                                    this.setState({ isLoading: false })
                                                }
                                            )
                                        },
                                    })
                                }}
                                value="Status"
                                color="primary"
                                inputProps={{ 'aria-label': 'Status' }}
                                disabled={propsColumn.isDisabled ? propsColumn.isDisabled(rowData) : null}
                            />
                        )
                        break
                    case 'edit':
                        newColumns[idx].hideTitle = this.props.columns[idx].hideTitle !== undefined ? this.props.columns[idx].hideTitle : true
                        newColumns[idx].render = newColumns[idx].render
                            ? newColumns[idx].render
                            : (rowData) => {
                                  return (
                                      <Button
                                          style={{ minWidth: '72px' }}
                                          onClick={() => {
                                              const priKey = propsColumn.priKey ? propsColumn.priKey : DEFAULT_PRIMARY_KEY
                                              propsColumn.component.OpenDialog(utils.ArrayToObject(this.state.data, priKey)[rowData[priKey]])
                                          }}
                                          color="primary"
                                          disabled={propsColumn.isDisabled ? propsColumn.isDisabled(rowData) : null}>
                                          <Edit />
                                          {this.t('edit')}
                                      </Button>
                                  )
                              }
                        break
                    case 'delete':
                        newColumns[idx].hideTitle = true
                        newColumns[idx].render = (rowData) => {
                            return (
                                <Button
                                    style={{ minWidth: '72px' }}
                                    onClick={() => {
                                        this.confirmDialog.OpenDialog({
                                            type: 'warn',
                                            msg: this.t('confirmDeleteMsg'),
                                            confirm: (_params) => {
                                                this.setState({ isLoading: true })
                                                const priKeyObj = propsColumn.priKey
                                                    ? {
                                                          [propsColumn.priKey]: rowData[propsColumn.priKey],
                                                      }
                                                    : {
                                                          [DEFAULT_PRIMARY_KEY]: rowData[DEFAULT_PRIMARY_KEY],
                                                      }
                                                utils.apiRequest(
                                                    'delete',
                                                    propsColumn.apiPath,
                                                    { ...priKeyObj },
                                                    (_data) => {
                                                        this.confirmDialog.closeDialog()
                                                        this.queryData()
                                                    },
                                                    (_error) => {
                                                        this.setState({ isLoading: false })
                                                    }

                                                )
                                            },
                                        })
                                    }}
                                    color="primary"
                                    disabled={propsColumn.isDisabled ? propsColumn.isDisabled(rowData) : null}>
                                    <Delete />
                                    {this.t('delete')}
                                </Button>
                            )
                        }
                        break
                    default:
                }
            }

            if (newColumns[idx].hideTitle) {
                newColumns[idx].title = ''
                newColumns[idx].sorting = false
            } else {
                newColumns[idx].title = propsColumn.title || utils.t(propsColumn.field, this.props.ownerName)
            }

            let textAlign = 'left'
            switch (propsColumn.type) {
                case 'number':
                case 'currency':
                case 'numberByThous':
                    textAlign = 'right'
                    break
                case 'icon':
                case 'action':
                case 'active':
                case 'delete':
                case 'edit':
                case 'is_support_single':
                case 'center':
                    textAlign = 'center'
                    break
                default:
            }

            newColumns[idx].cellStyle = {
                paddingTop: '5px',
                paddingBottom: '5px',
                paddingRight: '16px',
                minHeight: '45px',
                textAlign: textAlign,
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                ...propsColumn.cellStyle,
            }

            newColumns[idx].headerStyle = {
                textAlign: 'center',
                padding: '14px 0px 14px 18px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                borderTop: '1px solid rgba(224, 224, 224, 1)',
            }
        }

        this.setState(
            {
                columns: newColumns,
            },
            () => this.setDataDisplay() // for refresh DOM
        )
    }

    setActions() {
        if (!this.props.actions) {
            return
        }

        this.setState({ actions: this.props.actions })
    }

    setTranslateMap() {
        for (const idx in this.props.columns) {
            if (this.props.columns[idx].translate) {
                this.translateMap[this.props.columns[idx].field] = this.props.columns[idx].translate
            }
        }
    }

    render() {
        return (
            <React.Fragment>
                <MaterialTable
                    data={this.state.dataDisplay}
                    columns={this.state.columns}
                    options={{
                        exportButton: this.props.options ? this.props.options.export : false,
                        pageSize: this.state.pageSize,
                        pageSizeOptions: [10, 30, 50, 100],
                        showTitle: false,
                        searchFieldAlignment: 'left',
                        rowStyle: (rowData) => ({
                            height: '55px',
                            backgroundColor: rowData.parent === undefined ? '' : '#EEE',
                            ...(this.props.rowStyle ? this.props.rowStyle(rowData) : {}),
                        }),
                        headerStyle: {
                            whiteSpace: 'nowrap',
                        },
                        emptyRowsWhenPaging: false,
                        exportCsv: (columns, _data) => {
                            const exportColumns = columns.filter((column) => column.export)
                            const rows = []
                            const propsColumns = utils.ArrayToObject(this.props.columns, 'field')

                            /** 加入巢狀子層資料 */
                            const customExportData = _data.reduce((pre, item) => {
                                const appendToList = (rowItem) => {
                                    pre.push(rowItem)
                                    const hasChild = rowItem?.tableData?.childRows?.length ?? 0
                                    if (hasChild) {
                                        rowItem.tableData.childRows.map((childItem) => {
                                            return appendToList(childItem)
                                        })
                                    }
                                }
                                appendToList(item)
                                return pre
                            }, [])

                            const data = customExportData

                            const exportValue = (dataValue) => {
                                const value = typeof dataValue !== 'undefined' ? String(dataValue).replace(/"/g, '""') : ''
                                return typeof dataValue !== 'undefined' ? value : ''
                            }
                            /** 小數後位數 */
                            const decimal = this.props.auth.isCompleteDecimal ? 6 : 2
                            exportColumns.forEach((column) => {
                                data.forEach((elem, idx) => {
                                    if (!rows[idx]) rows[idx] = []

                                    if (column.render !== undefined) {
                                        const v = column.render(elem)
                                        if (typeof v === 'string') {
                                            rows[idx].push(exportValue(column.render(elem), propsColumns[column.field].type === 'datetime'))
                                            return
                                        }
                                    }

                                    switch (propsColumns[column.field].type) {
                                        case 'datetime':
                                            rows[idx].push(exportValue(utils.unixToDateTime(elem[column.field]), true))
                                            break
                                        case 'date':
                                            rows[idx].push(exportValue(utils.unixToDate(elem[column.field])))
                                            break
                                        case 'time':
                                            rows[idx].push(exportValue(utils.unixToTime(elem[column.field])))
                                            break
                                        case 'currency':
                                            rows[idx].push(
                                                exportValue(
                                                    utils.currencyFormatter(elem[column.field], decimal, utils.getCurrencyRate(this.state.currency))
                                                )
                                            )
                                            break
                                        case 'numberByThous':
                                            rows[idx].push(
                                                exportValue(
                                                    utils.currencyFormatter(elem[column.field], 0)
                                                )
                                            )
                                            break
                                        case 'platform':
                                            if (!elem[column.field] || !this.props.dataService.pfSubPfMap) rows[idx].push('')
                                            else if (elem[column.field] === 'all') rows[idx].push(this.t('allPlatform'))
                                            else if (elem[column.field].indexOf('public') > 0) {
                                                const pfId = elem[column.field].split('_')[0]
                                                rows[idx].push(
                                                    ('Platform.' + pfId === this.t('Platform.' + pfId) ? pfId : this.t('Platform.' + pfId)) +
                                                        ' ' +
                                                        this.t('public')
                                                )
                                            } else {
                                                rows[idx].push(exportValue(this.props.dataService.pfSubPfMap[elem[column.field]].name))
                                            }
                                            break
                                        default:
                                            rows[idx].push(exportValue(elem[column.field]))
                                    }
                                })
                            })
                            const xlsxColumns = []
                            /** 統計刪除欄位數量 */
                            let removeCount = 0
                            exportColumns.forEach((column, index)=>{
                                if (column.title) {
                                    xlsxColumns.push({name: column.title})
                                } else {
                                    rows.forEach((item, inx)=>{
                                        rows[inx].splice(index - removeCount, 1)
                                    })
                                    removeCount += 1
                                }
                            })

                            const workbook = new ExcelJs.Workbook();
                            const sheet = workbook.addWorksheet(`${this.props.ownerName}${this.state.currency ? `(${this.state.currency})` : ''}`);
                            
                            sheet.addTable({
                                name: this.props.ownerName, 
                                ref: 'A1',
                                columns: xlsxColumns,
                                rows
                            })
                        
                            workbook.xlsx.writeBuffer().then((content) => {
                                const link = document.createElement("a");
                                const blobData = new Blob([content], {
                                    type: "application/vnd.ms-excel;charset=utf-8;"
                                });
                                const defaultDownloadName = `${this.props.ownerName}${this.state.currency ? `(${this.state.currency})` : ''}`
                                const downloadName = `${this.props.options?.downloadName || defaultDownloadName}.xlsx`
                                link.download = downloadName;
                                link.href = URL.createObjectURL(blobData);
                                link.click();
                            })
                        },
                        ...this.props.options,
                    }}
                    localization={{
                        body: {
                            emptyDataSourceMessage: this.props.noData || this.t('noRecordsToDisplay'),
                        },
                        pagination: {
                            firstTooltip: '',
                            previousTooltip: '',
                            nextTooltip: '',
                            labelDisplayedRows: '{from}-{to} / {count}',
                            lastTooltip: '',
                            labelRowsSelect: this.t('rows'),
                        },
                        toolbar: {
                            searchTooltip: '',
                            searchPlaceholder: this.t('keyWordSearch'),
                            exportTitle: this.t('export'),
                            exportAriaLabel: this.t('export'),
                            exportName: this.t('exportAsExcel'),
                        },
                    }}
                    components={{
                        Action: (props) => (
                            <Button onClick={(event) => props.action.onClick(event, props.data)} color="primary">
                                <Icon>{props.action.icon}</Icon>
                                {this.t(props.action.text)}
                            </Button>
                        ),
                    }}
                    actions={this.state.actions}
                    title={this.props.title}
                    icons={{ Export: SaveAlt }}
                    isLoading={this.state.isLoading}
                    parentChildData={this.props.parentChildData}
                    {...this.props.materialProps}
                    tableRef={(ref) => {
                        this.materialTable = ref
                    }}
                    onChangeRowsPerPage={(pageSize) => {
                        this.setState({
                            pageSize: pageSize,
                        })
                    }}
                />
                <ConfirmMsgDialog onRef={(ref) => (this.confirmDialog = ref)} loading={this.state.isLoading} />
            </React.Fragment>
        )
    }
}

const styles = (_theme) => {
    return {}
}

Table.propTypes = {
    classes: PropTypes.object,
    ownerName: PropTypes.string,
    columns: PropTypes.array,
    options: PropTypes.object,
    actions: PropTypes.array,
    onRef: PropTypes.func,
    rowStyle: PropTypes.func,
    materialProps: PropTypes.object,
    parentChildData: PropTypes.func,
    queryDataArgs: PropTypes.object,
    dataService: PropTypes.object,
    title: PropTypes.string,
    auth: PropTypes.object,
    noData: PropTypes.string,
}

export default hoc(styles)(Table)
